import type { Event } from "@/types/Event";
import { formatApptDate, formatApptTime } from "@/components/A4Builder/appointment-record";

/**
 * FE mirror of the backend send-time event-variable enrichment
 * (`DocumentTemplatePerGuestService.enrichEventVarsPayload`, in turn parity with
 * the email context-enricher `enrichEvent`). The builders' "preview as guest"
 * resolves `{{...}}` tokens purely client-side against the guest object — but
 * event-level tokens like `{{eventDate}}`, `{{eventTime}}` and
 * `{{eventLocation}}` are NOT guest fields, so without this they render blank in
 * preview even though they resolve fine on a real send. This produces the same
 * key→value map the backend writes, to merge onto the preview guest.
 *
 * Keep date/time formatting in lockstep with the backend: `FormatStyle.LONG`
 * date + `FormatStyle.SHORT` time, guest-language locale (English fallback),
 * event timezone.
 */

const joinNonEmpty = (sep: string, ...parts: Array<string | undefined | null>): string =>
  parts
    .map((p) => (p ?? "").trim())
    .filter((p) => p.length > 0)
    .join(sep);

/** Mirror of backend `composeAddress`: "addr, addr2", "zip city", country. */
const composeAddress = (
  address?: string,
  address2?: string,
  zip?: string,
  city?: string,
  country?: string,
): string =>
  joinNonEmpty(
    ", ",
    joinNonEmpty(", ", address, address2),
    joinNonEmpty(" ", zip, city),
    country,
  );

/**
 * Build the event-level `{{variable}}` values for `event`, formatted for the
 * given guest `language` (backend uses the recipient's language; English when
 * unset). Empty sources are omitted so a missing field renders blank (matching
 * the backend `putIfPresent` contract), never a raw token.
 */
/**
 * Every key {@link buildEventVariables} can produce.
 *
 * <p>Absence of one of these on an enriched guest is INFORMATION, not ignorance:
 * the event was loaded whole and `put` deliberately skips empty sources, so a
 * missing `eventLocation` means the event has none — which is exactly what the
 * send will find. Guest tokens are the opposite case (a key the projection never
 * fetched says nothing), which is why the two are told apart.
 */
export const EVENT_VARIABLE_KEYS: ReadonlySet<string> = new Set([
  "eventWebsiteUrl", "eventName", "eventLocationName", "eventLocation",
  "eventCity", "eventZipCode", "eventCountry", "eventTimezone", "eventAddress",
  "companyName", "eventGuestLimit", "eventDate", "eventTime", "daysUntilEvent",
  "eventEndDate", "eventEndTime",
]);

export function buildEventVariables(
  event: Event | null | undefined,
  opts?: { language?: string | null; eventId?: string },
): Record<string, string> {
  const vars: Record<string, string> = {};

  // Default event-site host (mirror backend default; a custom domain would
  // override server-side — not resolved in preview).
  const eventId = opts?.eventId ?? event?.eventId;
  if (eventId) vars.eventWebsiteUrl = `https://${eventId}.veltric.app`;

  if (!event) return vars;

  const put = (key: string, value?: string | null) => {
    if (value != null && value.length > 0) vars[key] = value;
  };

  put("eventName", event.name);
  put("eventLocationName", event.locationName);
  put("eventLocation", event.locationName); // back-compat alias
  put("eventCity", event.city);
  put("eventZipCode", event.zipCode);
  put("eventCountry", event.country);
  put("companyName", event.companyName);
  put("eventTimezone", event.timeZone);
  put(
    "eventAddress",
    composeAddress(
      event.address,
      event.address2,
      event.zipCode,
      event.city,
      event.country,
    ),
  );
  if (event.globalGuestLimit != null) {
    vars.eventGuestLimit = String(event.globalGuestLimit);
  }

  const lang = opts?.language;
  const locale = lang && lang.length > 0 ? lang : "en";
  // UTC, never the browser's zone — `enrichEventVarsPayload` says why: a legacy
  // event without a zone must not render a different wall clock for every
  // operator, and the send picks UTC.
  const timeZone = event.timeZone || "UTC";
  // Reuse the humanize-parity formatters (formatApptDate/Time) so {{eventDate}}
  // / {{eventTime}} match the send byte-for-byte: long date, and short time with
  // the locale clock rules (de "14:30 Uhr", fr "14 h 30", forced-24h for
  // non-English). Plain Intl timeStyle here would drop those.
  const fmt = (date: Date, style: "date" | "time") =>
    style === "date"
      ? formatApptDate(date.toISOString(), locale, timeZone)
      : formatApptTime(date.toISOString(), locale, timeZone);

  if (event.start) {
    const start = new Date(event.start);
    vars.eventDate = fmt(start, "date");
    vars.eventTime = fmt(start, "time");
    // Ceiling, mirroring backend daysUntilEvent / humanize.ts daysUntil.
    const days = Math.ceil((start.getTime() - Date.now()) / 86_400_000);
    vars.daysUntilEvent = String(days);
  }
  if (event.end) {
    const end = new Date(event.end);
    vars.eventEndDate = fmt(end, "date");
    vars.eventEndTime = fmt(end, "time");
  }

  return vars;
}

/**
 * Merge event variables onto a guest for preview-time `{{...}}` resolution.
 * Returns a shallow copy (never mutates the source). Guest-owned keys win over
 * event keys so a guest field never gets shadowed.
 */
export function withEventVariables<T extends object>(
  guest: T,
  eventVars: Record<string, string>,
): T {
  return { ...eventVars, ...guest };
}
